import { useEffect, useRef, useState } from "react";
import { Play, Pause, Volume2, VolumeX, SkipBack, SkipForward } from "lucide-react";
import type { SoundEffect } from "../types";
import { supabase } from "../lib/supabase";
import { Button } from "@/components/ui/button";

interface AudioPlayerProps {
  currentTrack: SoundEffect | null;
  isPlaying: boolean;
  onPlay: () => void;
  onPause: () => void;
  onStop: () => void;
}

export function AudioPlayer({
  currentTrack,
  isPlaying,
  onPlay,
  onPause,
  onStop,
}: AudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const blobUrlRef = useRef<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.75);
  const [previousVolume, setPreviousVolume] = useState(0.75);
  const [isLoading, setIsLoading] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);

  // Function to create authenticated audio URL with headers
  const createAuthenticatedAudioSrc = async (downloadUrl: string): Promise<string> => {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new Error("No authentication token available");
    }

    console.log("Setting up authenticated audio stream for:", downloadUrl);
    
    // Create a full URL from the relative path
    const baseUrl = window.location.origin;
    const authUrl = new URL(downloadUrl, baseUrl);
    authUrl.searchParams.set('auth', session.access_token);
    
    return authUrl.toString();
  };

  // Set up audio element and event listeners
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => setIsLoading(false);
    const handleEnded = () => {
      onStop();
      setCurrentTime(0);
    };

    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("durationchange", handleDurationChange);
    audio.addEventListener("loadstart", handleLoadStart);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("ended", handleEnded);

    return () => {
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("durationchange", handleDurationChange);
      audio.removeEventListener("loadstart", handleLoadStart);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("ended", handleEnded);
    };
  }, [onStop]);

  // Handle play/pause state changes
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying && !isLoading) {
      audio.play().catch(console.error);
    } else {
      audio.pause();
    }
  }, [isPlaying, isLoading]);

  // Handle track changes - set up authenticated audio streaming
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !currentTrack) return;

    // Store the current playback position if we're just pausing/resuming the same track
    const currentSrc = audio.src;
    const isSameTrack = currentSrc && currentSrc.includes(currentTrack.id);
    
    console.log('Track change effect:', {
      currentTrackId: currentTrack.id,
      currentSrc,
      isSameTrack,
      currentTime: audio.currentTime
    });

    // Clean up previous blob URL if it exists
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    // Only reset and reload if it's a different track
    if (!isSameTrack) {
      setCurrentTime(0);
      setIsLoading(true);

      // Set up authenticated audio streaming URL
      createAuthenticatedAudioSrc(currentTrack.downloadUrl)
        .then((authenticatedUrl) => {
          console.log('Setting new audio source:', authenticatedUrl);
          audio.src = authenticatedUrl;
          audio.volume = volume;
          // Force load to start
          audio.load();
        })
        .catch((error) => {
          console.error("Failed to set up audio stream:", error);
          console.error("Download URL:", currentTrack.downloadUrl);
          setIsLoading(false);
          onStop(); // Stop playback on error
          alert(`Failed to load audio: ${error.message}`);
        });
    }
  }, [currentTrack?.id, onStop]); // Only depend on track ID, not the entire object

  // Handle volume changes separately
  useEffect(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.volume = volume;
    }
  }, [volume]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, []);

  const handlePlayPause = () => {
    if (isPlaying) {
      onPause();
    } else {
      onPlay();
    }
  };

  const handleSkipBack = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, audioRef.current.currentTime - 10);
    }
  };

  const handleSkipForward = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.min(duration, audioRef.current.currentTime + 10);
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    const audio = audioRef.current;
    if (!audio || !duration) return;

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = clickX / rect.width;
    const newTime = percentage * duration;

    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const handleVolumeChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));

    setVolume(percentage);
    if (audioRef.current) {
      audioRef.current.volume = percentage;
    }
  };

  const toggleMute = () => {
    if (volume > 0) {
      setPreviousVolume(volume);
      setVolume(0);
    } else {
      setVolume(previousVolume);
    }
  };

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;
  const volumePercentage = volume * 100;

  if (!currentTrack) {
    return null;
  }

  return (
    <div className="fixed bottom-0 left-72 right-0 bg-background/95 backdrop-blur-xl border-t border-border/50 px-8 py-4 z-50">
      <audio 
        ref={audioRef} 
        preload="metadata"
      />

      <div className="max-w-7xl mx-auto flex items-center justify-between">
        {/* Track Info */}
        <div className="flex items-center space-x-4 min-w-0 flex-1">
          <div className="flex items-center space-x-4">
            <h3 className="text-sm font-medium text-foreground truncate">
              {currentTrack.title}
            </h3>
            <span className="text-xs text-muted-foreground">
              {formatTime(currentTime)} / {duration > 0 ? formatTime(duration) : formatTime(currentTrack.duration)}
            </span>
          </div>
        </div>

        {/* Center Controls */}
        <div className="flex items-center space-x-6 mx-8">
          {/* Skip Back */}
          <Button
            onClick={handleSkipBack}
            variant="ghost"
            size="icon"
            disabled={!currentTrack}
          >
            <SkipBack size={18} />
          </Button>

          {/* Play/Pause */}
          <Button
            onClick={handlePlayPause}
            disabled={isLoading || !currentTrack}
            variant="default"
            size="icon"
            className="h-12 w-12 rounded-full shadow-lg hover:shadow-xl"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
            ) : isPlaying ? (
              <Pause size={20} />
            ) : (
              <Play size={20} className="ml-0.5" />
            )}
          </Button>

          {/* Skip Forward */}
          <Button
            onClick={handleSkipForward}
            variant="ghost"
            size="icon"
            disabled={!currentTrack}
          >
            <SkipForward size={18} />
          </Button>
        </div>

        {/* Progress Bar */}
        <div className="flex-1 mx-8">
          <div
            className={`relative h-1 bg-muted rounded-full cursor-pointer group ${
              currentTrack ? "cursor-pointer" : "cursor-not-allowed"
            }`}
            onClick={currentTrack ? handleSeek : undefined}
          >
            <div
              className="h-full bg-primary rounded-full transition-all duration-150"
              style={{ width: `${progressPercentage}%` }}
            />
            <div
              className="absolute top-1/2 w-3 h-3 bg-primary rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-lg"
              style={{
                left: `${progressPercentage}%`,
                transform: "translate(-50%, -50%)",
              }}
            />
          </div>
        </div>

        {/* Volume Controls */}
        <div className="flex items-center space-x-2 relative">
          <Button
            onClick={toggleMute}
            onMouseEnter={() => setShowVolumeSlider(true)}
            variant="ghost"
            size="icon"
          >
            {volume === 0 ? (
              <VolumeX size={18} />
            ) : (
              <Volume2 size={18} />
            )}
          </Button>
          
          {/* Volume Slider */}
          <div 
            className={`
              absolute right-0 bottom-full mb-3 p-4 bg-card rounded-lg border border-border shadow-xl
              transition-all duration-200 ${showVolumeSlider ? 'opacity-100 visible' : 'opacity-0 invisible'}
            `}
            onMouseEnter={() => setShowVolumeSlider(true)}
            onMouseLeave={() => setShowVolumeSlider(false)}
          >
            <div className="w-32 space-y-3">
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>Volume</span>
                <span>{Math.round(volume * 100)}%</span>
              </div>
              <div
                className="relative h-1 bg-muted rounded-full cursor-pointer group"
                onClick={handleVolumeChange}
              >
                <div
                  className="h-full bg-primary rounded-full transition-all duration-150"
                  style={{ width: `${volumePercentage}%` }}
                />
                <div
                  className="absolute top-1/2 w-3 h-3 bg-primary rounded-full transition-all duration-200 shadow-md"
                  style={{
                    left: `${volumePercentage}%`,
                    transform: "translate(-50%, -50%)",
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}